/**
 * Efficiency Helpers Module
 *
 * Canonical helpers for efficiency formatting, band derivation, and system modifiers.
 * This module provides a single source of truth for efficiency semantics:
 *
 * - Seasonal efficiency: 0-1 scale (e.g., 0.94 = 94%)
 * - SEDBUK = Seasonal Efficiency of Domestic Boilers in the UK
 * - Band A-G: derived from seasonal efficiency using fixed thresholds
 *
 * IMPORTANT: In UK domestic context, SEDBUK IS seasonal efficiency.
 * There is no meaningful distinction - they are the same measurement methodology.
 * All efficiency values flow through the canonical SeasonalEfficiency type.
 */

import { SeasonalEfficiency, SeasonalEfficiencySource } from './types';

// ==================== TYPES ====================

export type SedbukBand = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

export type EfficiencySource = 'GC' | 'MAKE_MODEL' | 'USER' | 'EPC_BAND' | 'ASSUMED';

export type CyclingRisk = 'low' | 'medium' | 'high';

export interface EfficiencyData {
  sedbukSeasonal?: number;      // 0-1 scale (canonical SEDBUK value)
  sedbukBand?: SedbukBand;      // Derived or direct from lookup
  erpSpaceHeating?: number;     // 0-1 scale (only if explicitly sourced)
  source: EfficiencySource;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  matchedBoiler?: {
    make: string;
    model: string;
    gcNumber: string;
  };
}

export interface CyclingLossData {
  risk: CyclingRisk;
  penalty: number;              // 0-1 scale (e.g., 0.06 = 6% of heating fuel wasted due to cycling)
  oversizingRatio?: number;     // Boiler output / heat loss (if known)
  reason: string;
}

// ==================== SEDBUK BAND THRESHOLDS ====================

/**
 * SEDBUK band thresholds (0-1 scale)
 *
 * These are the official SEDBUK 2009 thresholds used for band derivation from numeric efficiency.
 * For display/presentation labels, use SEDBUK_BAND_RANGES instead.
 *
 * Thresholds:
 * - A: >= 90%
 * - B: 86-89.9%
 * - C: 82-85.9%
 * - D: 78-81.9%
 * - E: 74-77.9%
 * - F: 70-73.9%
 * - G: < 70%
 */
export const SEDBUK_BAND_THRESHOLDS: Record<SedbukBand, { min: number; max: number }> = {
  A: { min: 0.90, max: 1.00 },
  B: { min: 0.86, max: 0.899 },
  C: { min: 0.82, max: 0.859 },
  D: { min: 0.78, max: 0.819 },
  E: { min: 0.74, max: 0.779 },
  F: { min: 0.70, max: 0.739 },
  G: { min: 0.00, max: 0.699 },
};

/**
 * SEDBUK band ranges with customer-facing labels
 * 
 * Authoritative band definitions with min/max ranges and display labels.
 * These are the single source of truth for band presentation to customers.
 * 
 * Note: SEDBUK_BAND_THRESHOLDS uses precise decimal values for band derivation,
 * while SEDBUK_BAND_RANGES uses simpler boundaries (0.90 vs 0.899) for clarity.
 * Both are maintained separately to serve different purposes:
 * - SEDBUK_BAND_THRESHOLDS: for deriveSedbukBand() logic (precise boundaries)
 * - SEDBUK_BAND_RANGES: for customer-facing display (simplified boundaries + labels)
 */
export const SEDBUK_BAND_RANGES = {
  A: { min: 0.90, max: null, label: "90% or more" },
  B: { min: 0.86, max: 0.90, label: "86%–90%" },
  C: { min: 0.82, max: 0.86, label: "82%–86%" },
  D: { min: 0.78, max: 0.82, label: "78%–82%" },
  E: { min: 0.74, max: 0.78, label: "74%–78%" },
  F: { min: 0.70, max: 0.74, label: "70%–74%" },
  G: { min: null, max: 0.70, label: "Below 70%" },
} as const;

/**
 * Typical efficiency value for each band (used when only band is known)
 *
 * These are midpoint-ish values for calculation purposes.
 * G band uses 0.65 as the typical value for "below 70%" systems.
 */
export const SEDBUK_BAND_TYPICAL: Record<SedbukBand, number> = {
  A: 0.92,  // Midpoint of A band (90%+)
  B: 0.88,  // Midpoint of B band (86%–90%)
  C: 0.84,  // Midpoint of C band (82%–86%)
  D: 0.80,  // Midpoint of D band (78%–82%)
  E: 0.76,  // Midpoint of E band (74%–78%)
  F: 0.72,  // Midpoint of F band (70%–74%)
  G: 0.65,  // Below 70% - conservative typical value for old/inefficient systems
};

// ==================== BOILER TYPE MODIFIERS ====================

/**
 * Boiler type efficiency modifiers
 *
 * These represent real-world distribution losses and system characteristics:
 * - System boilers: Sealed system, modern controls, minimal distribution losses
 * - Regular boilers: Typically open vented (more pipework to loft/tank, higher losses)
 *   Note: Regular with unvented cylinder is a sealed system but still has slightly higher losses
 *
 * Values are applied as multipliers to the base efficiency.
 */
export const BOILER_TYPE_MODIFIERS: Record<string, { factor: number; reason: string }> = {
  system: {
    factor: 1.00,  // No penalty - sealed system, good controls
    reason: 'System boiler: sealed system with minimal distribution losses',
  },
  regular: {
    factor: 0.97,  // 3% penalty for additional distribution losses
    reason: 'Regular boiler: typically open-vented or has additional distribution losses',
  },
  combi: {
    factor: 1.00,  // No penalty - direct heating, no storage losses
    reason: 'Combi boiler: no cylinder storage losses',
  },
  storage_combi: {
    factor: 0.99,  // 1% penalty for small storage losses
    reason: 'Storage combi: minimal storage losses from integral store',
  },
};

// ==================== CYLINDER TYPE STANDING LOSSES ====================

/**
 * Cylinder standing loss values by strategy and variant (kWh/day)
 *
 * Standing losses represent heat lost from the cylinder when not in use.
 * Modern cylinders with better insulation have lower losses.
 * Mixergy smart cylinders use stratification to heat only what's needed.
 */
export const CYLINDER_STANDING_LOSS_BY_STRATEGY: Record<string, Record<string, { kwhPerDay: number; reason: string }>> = {
  open_vented: {
    standard: {
      kwhPerDay: 1.8,
      reason: 'Open-vented cylinder: older insulation, loft location, less controlled temperature',
    },
    mixergy: {
      kwhPerDay: 0.8,
      reason: 'Mixergy open-vented: smart stratification but open-vented constraints',
    },
  },
  unvented: {
    standard: {
      kwhPerDay: 1.2,
      reason: 'Unvented cylinder: modern insulation, better temperature control',
    },
    mixergy: {
      kwhPerDay: 0.6,
      reason: 'Mixergy unvented: smart stratification heats only what\'s needed, reduces losses by ~70%',
    },
  },
  heat_battery: {
    standard: {
      kwhPerDay: 0.3,
      reason: 'Heat battery (e.g., Sunamp): phase-change material with excellent insulation',
    },
  },
  thermal_store: {
    standard: {
      kwhPerDay: 2.0,
      reason: 'Thermal store: larger volume, multi-source capable but higher standing losses',
    },
  },
  on_demand: {
    standard: {
      kwhPerDay: 0.0,
      reason: 'No cylinder: on-demand heating has no standing losses',
    },
  },
  none: {
    standard: {
      kwhPerDay: 0.0,
      reason: 'No hot water system',
    },
  },
};

/**
 * Legacy cylinder type standing loss values (kWh/day) - DEPRECATED
 * Use getCylinderStandingLossByStrategy instead
 * 
 * Kept for backward compatibility with existing code that uses string keys
 */
export const CYLINDER_STANDING_LOSS: Record<string, { kwhPerDay: number; reason: string }> = {
  open_vented: CYLINDER_STANDING_LOSS_BY_STRATEGY.open_vented.standard,
  unvented: CYLINDER_STANDING_LOSS_BY_STRATEGY.unvented.standard,
  mixergy_unvented: CYLINDER_STANDING_LOSS_BY_STRATEGY.unvented.mixergy,
  mixergy_open: CYLINDER_STANDING_LOSS_BY_STRATEGY.open_vented.mixergy,
  heat_battery: CYLINDER_STANDING_LOSS_BY_STRATEGY.heat_battery.standard,
  thermal_store: CYLINDER_STANDING_LOSS_BY_STRATEGY.thermal_store.standard,
  on_demand: CYLINDER_STANDING_LOSS_BY_STRATEGY.on_demand.standard,
};

// ==================== CYCLING LOSS PENALTIES ====================

/**
 * Cycling loss penalty values by risk level (0-1 scale)
 *
 * These represent the fraction of heating fuel wasted due to short-cycling:
 * - Ignition/purge losses
 * - Warm-up losses before condensing
 * - Stop/start inefficiencies
 *
 * Risk levels are determined by boiler oversizing ratio:
 * - Low: < 1.4x (reasonable sizing)
 * - Medium: 1.4-2.0x (moderately oversized)
 * - High: >= 2.0x (significantly oversized, frequent short-cycling)
 */
export const CYCLING_LOSS_PENALTY_BY_RISK: Record<CyclingRisk, number> = {
  low: 0.01,     // 1% waste - minimal short-cycling
  medium: 0.03,  // 3% waste - moderate short-cycling
  high: 0.06,    // 6% waste - significant short-cycling
};

/**
 * Oversizing ratio thresholds for cycling risk assessment
 *
 * Ratio = boilerOutputKw / heatLossKw
 */
export const CYCLING_RISK_THRESHOLDS = {
  HIGH_THRESHOLD: 2.0,    // >= 2.0x = high risk
  MEDIUM_THRESHOLD: 1.4,  // >= 1.4x = medium risk, < 1.4x = low risk
};

/**
 * Maximum load shaping benefit parameters
 *
 * These cap the amount of DHW heating that can be "shifted" to reduce cycling:
 * - maxShiftFraction: max fraction of daily DHW that can be shifted (30%)
 * - maxShiftAbsoluteKwh: absolute cap on daily shift amount (2 kWh)
 * - maxPenaltyReduction: max reduction in cycling penalty (50%)
 */
export const LOAD_SHAPING_LIMITS = {
  maxShiftFraction: 0.3,      // Can shift up to 30% of daily DHW heating
  maxShiftAbsoluteKwh: 2.0,   // Absolute cap of 2 kWh/day
  maxPenaltyReduction: 0.5,   // Can reduce cycling penalty by up to 50%
};

// ==================== CANONICAL FORMATTING FUNCTIONS ====================

/**
 * Format SEDBUK band with customer-facing label
 * 
 * @param band - SEDBUK band A-G
 * @returns Formatted string e.g., "Band G: Below 70%"
 */
export function formatSedbukBandLabel(band: SedbukBand): string {
  return `Band ${band}: ${SEDBUK_BAND_RANGES[band].label}`;
}

/**
 * Format efficiency as percentage (0-1 → integer %)
 *
 * Uses Math.round for consistency. This is the ONLY function that should
 * convert 0-1 efficiency to a display percentage.
 *
 * @param efficiency - Efficiency value (0-1 scale)
 * @returns Integer percentage (0-100)
 */
export function formatSeasonalPct(efficiency: number): number {
  return Math.round(efficiency * 100);
}

/**
 * Derive SEDBUK band from efficiency value
 *
 * Uses fixed thresholds to derive band A-G from 0-1 efficiency.
 * This is the ONLY function that should derive bands from efficiency values.
 *
 * @param efficiency - Efficiency value (0-1 scale)
 * @returns SEDBUK band A-G
 */
export function deriveSedbukBand(efficiency: number): SedbukBand {
  if (efficiency >= 0.90) return 'A';
  if (efficiency >= 0.86) return 'B';
  if (efficiency >= 0.82) return 'C';
  if (efficiency >= 0.78) return 'D';
  if (efficiency >= 0.74) return 'E';
  if (efficiency >= 0.70) return 'F';
  return 'G';
}

/**
 * Format efficiency for display as "SEDBUK band X (Y%)" or "Band X: range"
 *
 * When we only know the band (no numeric efficiency), shows the band range label.
 * This prevents "NaN%" from ever appearing in the output.
 *
 * @param efficiency - Efficiency value (0-1 scale), may be NaN if only band is known
 * @param band - Optional pre-computed band (uses deriveSedbukBand if not provided)
 * @returns Formatted string e.g., "SEDBUK band A (94%)" or "Band G: Below 70%"
 */
export function formatSedbukDisplay(efficiency: number, band?: SedbukBand): string {
  const derivedBand = band || deriveSedbukBand(efficiency);
  
  // If efficiency is not a valid number (NaN, Infinity, etc.), show band label only
  if (!isFinite(efficiency) || !isValidEfficiency(efficiency)) {
    return formatSedbukBandLabel(derivedBand);
  }
  
  const pct = formatSeasonalPct(efficiency);
  return `SEDBUK band ${derivedBand} (${pct}%)`;
}

/**
 * Get efficiency from SEDBUK band (when only band is known)
 *
 * @param band - SEDBUK band A-G
 * @returns Typical efficiency for that band (0-1 scale)
 */
export function getEfficiencyFromBand(band: SedbukBand): number {
  return SEDBUK_BAND_TYPICAL[band];
}

// ==================== MODIFIER APPLICATION ====================

/**
 * Apply boiler type modifier to base efficiency
 *
 * @param baseEfficiency - Base SEDBUK efficiency (0-1)
 * @param boilerType - Boiler type (system, regular, combi, storage_combi)
 * @returns Object with adjusted efficiency and modifier applied
 */
export function applyBoilerTypeModifier(
  baseEfficiency: number,
  boilerType: string
): { adjustedEfficiency: number; modifier: typeof BOILER_TYPE_MODIFIERS[string] | null } {
  const modifier = BOILER_TYPE_MODIFIERS[boilerType];

  if (!modifier) {
    return { adjustedEfficiency: baseEfficiency, modifier: null };
  }

  return {
    adjustedEfficiency: baseEfficiency * modifier.factor,
    modifier,
  };
}

/**
 * Get cylinder standing loss for a given cylinder type
 *
 * @param cylinderType - Cylinder type
 * @returns Object with standing loss and reason
 */
export function getCylinderStandingLoss(
  cylinderType: string
): { kwhPerDay: number; reason: string } {
  const loss = CYLINDER_STANDING_LOSS[cylinderType];

  if (!loss) {
    // Default to unvented if unknown
    return {
      kwhPerDay: 1.5,
      reason: 'Unknown cylinder type: using default standing loss of 1.5 kWh/day',
    };
  }

  return loss;
}

/**
 * Get cylinder standing loss by strategy and variant
 *
 * @param strategy - Hot water strategy (e.g., 'open_vented', 'unvented')
 * @param variant - Cylinder variant (e.g., 'standard', 'mixergy')
 * @returns Object with standing loss and reason
 */
export function getCylinderStandingLossByStrategy(
  strategy: string,
  variant: string = 'standard'
): { kwhPerDay: number; reason: string } {
  const strategyLosses = CYLINDER_STANDING_LOSS_BY_STRATEGY[strategy];

  if (!strategyLosses) {
    // Default to unvented standard if unknown strategy
    const fallbackValue = CYLINDER_STANDING_LOSS_BY_STRATEGY.unvented?.standard?.kwhPerDay || 1.2;
    return {
      kwhPerDay: fallbackValue,
      reason: `Unknown hot water strategy '${strategy}': using default standing loss of ${fallbackValue} kWh/day`,
    };
  }

  const variantLoss = strategyLosses[variant];

  if (!variantLoss) {
    // Fall back to standard variant if unknown variant
    const standardLoss = strategyLosses.standard;
    if (standardLoss) {
      return {
        kwhPerDay: standardLoss.kwhPerDay,
        reason: `Unknown variant '${variant}' for strategy '${strategy}': using standard variant (${standardLoss.kwhPerDay} kWh/day)`,
      };
    }

    // Ultimate fallback
    const fallbackValue = CYLINDER_STANDING_LOSS_BY_STRATEGY.unvented?.standard?.kwhPerDay || 1.2;
    return {
      kwhPerDay: fallbackValue,
      reason: `Unknown strategy '${strategy}' and variant '${variant}': using default standing loss of ${fallbackValue} kWh/day`,
    };
  }

  return variantLoss;
}

// ==================== EFFICIENCY RESOLUTION ====================

/**
 * Resolve efficiency from various input sources
 *
 * Priority order:
 * 1. Direct GC number lookup (HIGH confidence)
 * 2. Make + model lookup (MEDIUM confidence)
 * 3. User-provided SEDBUK band (MEDIUM confidence)
 * 4. EPC band (LOW confidence)
 * 5. Assumed default (LOW confidence)
 *
 * GUARANTEE: Always returns a finite numeric efficiency. Never returns NaN.
 *
 * @param input - Input data for resolution
 * @returns EfficiencyData with resolved values
 */
export function resolveEfficiency(input: {
  sedbukSeasonalFromLookup?: number;
  sedbukBandFromLookup?: SedbukBand;
  userSedbukBand?: SedbukBand;
  epcBand?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
  source?: EfficiencySource;
  matchedBoiler?: { make: string; model: string; gcNumber: string };
}): EfficiencyData {
  // 1. GC or model lookup - highest confidence
  if (input.sedbukSeasonalFromLookup !== undefined && isFinite(input.sedbukSeasonalFromLookup) && isValidEfficiency(input.sedbukSeasonalFromLookup)) {
    const band = input.sedbukBandFromLookup || deriveSedbukBand(input.sedbukSeasonalFromLookup);
    return {
      sedbukSeasonal: input.sedbukSeasonalFromLookup,
      sedbukBand: band,
      source: input.source || 'GC',
      confidence: input.source === 'MAKE_MODEL' ? 'MEDIUM' : 'HIGH',
      matchedBoiler: input.matchedBoiler,
    };
  }

  // 2. If we have a band from lookup but no numeric efficiency, use typical value for that band
  if (input.sedbukBandFromLookup) {
    const efficiency = getEfficiencyFromBand(input.sedbukBandFromLookup);
    return {
      sedbukSeasonal: efficiency,
      sedbukBand: input.sedbukBandFromLookup,
      source: input.source || 'GC',
      confidence: input.source === 'MAKE_MODEL' ? 'MEDIUM' : 'HIGH',
      matchedBoiler: input.matchedBoiler,
    };
  }

  // 3. User-provided SEDBUK band
  if (input.userSedbukBand) {
    const efficiency = getEfficiencyFromBand(input.userSedbukBand);
    return {
      sedbukSeasonal: efficiency,
      sedbukBand: input.userSedbukBand,
      source: 'USER',
      confidence: 'MEDIUM',
    };
  }

  // 4. EPC band (less accurate than SEDBUK)
  if (input.epcBand) {
    const efficiency = getEfficiencyFromBand(input.epcBand as SedbukBand);
    return {
      sedbukSeasonal: efficiency,
      sedbukBand: input.epcBand as SedbukBand,
      source: 'EPC_BAND',
      confidence: 'LOW',
    };
  }

  // 5. Default fallback - ALWAYS return a valid numeric efficiency
  return {
    sedbukSeasonal: 0.78,  // EPC D equivalent (conservative)
    sedbukBand: 'D',
    source: 'ASSUMED',
    confidence: 'LOW',
  };
}

// ==================== CANONICAL SEASONAL EFFICIENCY ====================

/**
 * Source description mappings for human-readable output
 */
const SOURCE_DESCRIPTIONS: Record<SeasonalEfficiencySource, (band?: SedbukBand, value?: number) => string> = {
  'SEDBUK_GC': (band, value) => `SEDBUK band ${band || '?'} (${formatSeasonalPct(value || 0)}%) from GC number lookup`,
  'SEDBUK_MAKE_MODEL': (band, value) => `SEDBUK band ${band || '?'} (${formatSeasonalPct(value || 0)}%) from make/model lookup`,
  'SEDBUK_BAND': (band, value) => `SEDBUK band ${band || '?'} (${formatSeasonalPct(value || 0)}%) from user selection`,
  'EPC_BAND': (band, value) => `EPC band ${band || '?'} (${formatSeasonalPct(value || 0)}%)`,
  'USER': (_, value) => `User-provided efficiency (${formatSeasonalPct(value || 0)}%)`,
  'ASSUMED': () => `Assumed efficiency 78% (no efficiency data provided)`,
};

/**
 * Resolve seasonal efficiency from various input sources.
 *
 * This is THE canonical function for determining boiler efficiency.
 * All downstream calculations must use the returned SeasonalEfficiency object.
 *
 * Resolution priority:
 * 1. GC number lookup result - highest confidence
 * 2. Make/model lookup result - high confidence
 * 3. User-provided seasonal efficiency value - medium confidence
 * 4. User-selected SEDBUK band (mapped to typical value) - medium confidence
 * 5. EPC band (less accurate) - low confidence
 * 6. Assumed default (78%) - low confidence
 *
 * GUARANTEE: Always returns a valid SeasonalEfficiency. Never returns undefined or NaN.
 *
 * @param input - Input data for resolution
 * @returns Canonical SeasonalEfficiency object
 */
export function resolveSeasonalEfficiency(input: {
  // From GC/make-model lookup (highest priority)
  gcLookupResult?: {
    value: number;
    band?: SedbukBand;
    source: 'gc_lookup' | 'model_lookup';
  };
  // User-provided values
  userSeasonalEfficiency?: number;  // Direct value (0-1)
  userSedbukBand?: SedbukBand;      // Band selection (A-G)
  epcBand?: SedbukBand;             // From EPC certificate
}): SeasonalEfficiency {

  // 1. GC number lookup - highest confidence
  if (input.gcLookupResult?.value !== undefined &&
      isFinite(input.gcLookupResult.value) &&
      isValidEfficiency(input.gcLookupResult.value)) {
    const value = input.gcLookupResult.value;
    const band = input.gcLookupResult.band || deriveSedbukBand(value);
    const source: SeasonalEfficiencySource = input.gcLookupResult.source === 'model_lookup'
      ? 'SEDBUK_MAKE_MODEL'
      : 'SEDBUK_GC';

    return {
      value,
      band,
      source,
      confidence: source === 'SEDBUK_GC' ? 'high' : 'medium',
      sourceDescription: SOURCE_DESCRIPTIONS[source](band, value),
    };
  }

  // 2. User-selected SEDBUK band (with or without pre-computed efficiency)
  // If band is provided, this is a band selection - use SEDBUK_BAND source
  // The sedbukSeasonal value may come from the UI's band→% mapping
  if (input.userSedbukBand && isValidSedbukBand(input.userSedbukBand)) {
    // Use provided efficiency if available, otherwise map from band
    const value = (input.userSeasonalEfficiency !== undefined &&
                   isFinite(input.userSeasonalEfficiency) &&
                   isValidEfficiency(input.userSeasonalEfficiency))
      ? input.userSeasonalEfficiency
      : getEfficiencyFromBand(input.userSedbukBand);
    const band = input.userSedbukBand;

    return {
      value,
      band,
      source: 'SEDBUK_BAND',
      confidence: 'medium',
      sourceDescription: SOURCE_DESCRIPTIONS['SEDBUK_BAND'](band, value),
    };
  }

  // 3. User-provided seasonal efficiency value (standalone, no band)
  // This is only used when user provides a raw efficiency without selecting a band
  if (input.userSeasonalEfficiency !== undefined &&
      isFinite(input.userSeasonalEfficiency) &&
      isValidEfficiency(input.userSeasonalEfficiency)) {
    const value = input.userSeasonalEfficiency;
    const band = deriveSedbukBand(value);

    return {
      value,
      band,
      source: 'USER',
      confidence: 'medium',
      sourceDescription: SOURCE_DESCRIPTIONS['USER'](band, value),
    };
  }

  // 4. EPC band (less accurate than SEDBUK)
  if (input.epcBand && isValidSedbukBand(input.epcBand)) {
    const value = getEfficiencyFromBand(input.epcBand);
    const band = input.epcBand;

    return {
      value,
      band,
      source: 'EPC_BAND',
      confidence: 'low',
      sourceDescription: SOURCE_DESCRIPTIONS['EPC_BAND'](band, value),
    };
  }

  // 5. Default fallback - ALWAYS return a valid efficiency
  return {
    value: 0.78,  // EPC D equivalent (conservative)
    band: 'D',
    source: 'ASSUMED',
    confidence: 'low',
    sourceDescription: SOURCE_DESCRIPTIONS['ASSUMED'](),
  };
}

/**
 * Format seasonal efficiency for display.
 *
 * Examples:
 * - "Seasonal efficiency: 92% (SEDBUK band A, high confidence)"
 * - "Seasonal efficiency: 78% (assumed, low confidence)"
 *
 * @param eff - SeasonalEfficiency object
 * @returns Human-readable string
 */
export function formatSeasonalEfficiencyDisplay(eff: SeasonalEfficiency): string {
  const pct = formatSeasonalPct(eff.value);
  const bandStr = eff.band ? `, band ${eff.band}` : '';
  return `Seasonal efficiency: ${pct}%${bandStr} (${eff.confidence} confidence)`;
}

// ==================== VALIDATION ====================

/**
 * Validate efficiency value is in valid range
 *
 * @param efficiency - Efficiency value to validate
 * @returns True if valid (0-1 range), false otherwise
 */
export function isValidEfficiency(efficiency: number): boolean {
  return efficiency >= 0 && efficiency <= 1;
}

/**
 * Validate SEDBUK band is valid
 *
 * @param band - Band to validate
 * @returns True if valid (A-G), false otherwise
 */
export function isValidSedbukBand(band: string): band is SedbukBand {
  return ['A', 'B', 'C', 'D', 'E', 'F', 'G'].includes(band);
}

// ==================== CYCLING LOSS ASSESSMENT ====================

/**
 * Determine cycling risk from boiler oversizing ratio
 *
 * @param oversizingRatio - Boiler output kW / heat loss kW
 * @returns Cycling risk level and default penalty
 */
export function determineCyclingRisk(oversizingRatio: number): CyclingLossData {
  if (oversizingRatio >= CYCLING_RISK_THRESHOLDS.HIGH_THRESHOLD) {
    return {
      risk: 'high',
      penalty: CYCLING_LOSS_PENALTY_BY_RISK.high,
      oversizingRatio,
      reason: `Boiler significantly oversized (${oversizingRatio.toFixed(1)}x heat loss) - high short-cycling risk`,
    };
  }
  
  if (oversizingRatio >= CYCLING_RISK_THRESHOLDS.MEDIUM_THRESHOLD) {
    return {
      risk: 'medium',
      penalty: CYCLING_LOSS_PENALTY_BY_RISK.medium,
      oversizingRatio,
      reason: `Boiler moderately oversized (${oversizingRatio.toFixed(1)}x heat loss) - moderate short-cycling risk`,
    };
  }
  
  return {
    risk: 'low',
    penalty: CYCLING_LOSS_PENALTY_BY_RISK.low,
    oversizingRatio,
    reason: `Boiler reasonably sized (${oversizingRatio.toFixed(1)}x heat loss) - minimal short-cycling risk`,
  };
}

/**
 * Calculate load shaping benefit for Mixergy cylinders
 *
 * Load shaping allows the boiler to run longer, steadier runs by adding DHW charging
 * when space heating demand is low. This reduces cycling losses.
 *
 * @param input - Load shaping calculation inputs
 * @returns Adjusted cycling penalty and evidence data
 */
export function calculateLoadShapingBenefit(input: {
  baseCyclingPenalty: number;
  dhwUsefulKwhPerDay: number;
  dhwHeadroomKwh: number;
  isMixergy: boolean;
  hasStoredDHW: boolean;
}): {
  adjustedPenalty: number;
  shiftKwhPerDay: number;
  maxShiftKwhPerDay: number;
  penaltyReduction: number;
  reason: string;
} {
  // Only Mixergy with DHW storage can provide load shaping benefit
  if (!input.isMixergy || !input.hasStoredDHW) {
    return {
      adjustedPenalty: input.baseCyclingPenalty,
      shiftKwhPerDay: 0,
      maxShiftKwhPerDay: 0,
      penaltyReduction: 0,
      reason: 'Load shaping not available (requires Mixergy cylinder with storage)',
    };
  }

  // Calculate maximum allowed DHW shift per day
  const maxShiftKwhPerDay = Math.min(
    input.dhwUsefulKwhPerDay * LOAD_SHAPING_LIMITS.maxShiftFraction,
    LOAD_SHAPING_LIMITS.maxShiftAbsoluteKwh
  );

  // Actual shift is limited by available headroom
  const shiftKwhPerDay = Math.min(maxShiftKwhPerDay, input.dhwHeadroomKwh);

  // If no shift possible, no benefit
  if (shiftKwhPerDay <= 0 || maxShiftKwhPerDay <= 0) {
    return {
      adjustedPenalty: input.baseCyclingPenalty,
      shiftKwhPerDay: 0,
      maxShiftKwhPerDay,
      penaltyReduction: 0,
      reason: 'Load shaping benefit unavailable (no DHW headroom)',
    };
  }

  // Calculate penalty reduction (proportional to shift utilization)
  const shiftUtilization = shiftKwhPerDay / maxShiftKwhPerDay; // 0-1
  const maxPenaltyReduction = input.baseCyclingPenalty * LOAD_SHAPING_LIMITS.maxPenaltyReduction;
  const penaltyReduction = shiftUtilization * maxPenaltyReduction;

  const adjustedPenalty = Math.max(0, input.baseCyclingPenalty - penaltyReduction);

  const reductionPct = input.baseCyclingPenalty > 0 
    ? ((penaltyReduction / input.baseCyclingPenalty) * 100).toFixed(0)
    : '0';

  return {
    adjustedPenalty,
    shiftKwhPerDay,
    maxShiftKwhPerDay,
    penaltyReduction,
    reason: `Mixergy load-shaping reduces cycling penalty by ${reductionPct}% (shifts ${shiftKwhPerDay.toFixed(1)} kWh/day into useful DHW storage)`,
  };
}
